import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  XIcon,
  SparklesIcon,
  Loader2Icon,
  ListChecksIcon,
  SendIcon,
  AlertTriangleIcon,
  Trash2Icon,
  BarChart3Icon,
  SearchIcon,
  PencilIcon,
  StarIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CheckCircle2Icon,
  InfoIcon,
  PlusIcon,
  TagIcon,
  ListOrderedIcon,
  CornerDownRightIcon,
  MousePointerClickIcon,
  LayersIcon,
  Wand2Icon,
  HelpCircleIcon,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { tenantApi } from '../../lib/tenant-api';
import type {
  QuestionOp,
  QuestionOpsPlanParams,
  PrimerQuestionRequest,
} from '../../lib/api-types';
import { AiIcon } from '../AIAssistantChat';
import MarkdownRenderer from '../MarkdownRenderer';
import OpDiffCard, { OpStatus } from './OpDiffCard';
import { loadJSON, saveJSON, removeKey } from '../../lib/chatStorage';

interface QuestionAIAssistantDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Currently selected question ids — grounding for "these" references. */
  questionIds?: string[];
  /** Each tab's visible questions in display order — makes "question N in the X tab" resolve correctly. */
  tabOrders?: Record<string, string[]>;
  /** The tab the admin is currently viewing. */
  activeTab?: string;
  /** The list's current filter — grounding for "the filtered ones". */
  filters?: QuestionOpsPlanParams['filters'];
  /** Called after ops are applied so the page can refresh its question list. */
  onApplied: () => void | Promise<void>;
}

type ChatMessage =
  | { id: string; role: 'user'; text: string }
  | { id: string; role: 'assistant'; text: string; warnings: string[]; suggestions: string[]; kind?: 'applied' };

// Persisted across reloads so an in-progress review isn't lost.
const QKEY = 'qaichat:v1';
type Decision = 'approved' | 'rejected';
interface QPersist {
  messages: ChatMessage[];
  pendingOps: QuestionOp[];
  decisions: Record<string, Decision>;
  statuses: Record<string, OpStatus>;
  wide: boolean;
}

// Starter prompts that showcase the full range: overview → gap analysis →
// surgical edit → bulk change.
const SUGGESTIONS: { icon: React.FC<any>; text: string }[] = [
  { icon: BarChart3Icon, text: 'Give me an overview of my question library' },
  { icon: SearchIcon, text: 'Find questions with thin, missing, or duplicate answers' },
  { icon: PencilIcon, text: 'Tighten my pricing answers without changing anything else' },
  { icon: StarIcon, text: 'Make every FAQ question first-class' },
];

// Capability titles shown in the "What I can do" modal (titles only).
const CAPABILITIES: { title: string; Icon: React.FC<any> }[] = [
  { title: 'Ask & analyze', Icon: BarChart3Icon },
  { title: 'Create questions', Icon: PlusIcon },
  { title: 'Edit questions', Icon: HelpCircleIcon },
  { title: 'Edit answers', Icon: PencilIcon },
  { title: 'Toggle flags', Icon: StarIcon },
  { title: 'Manage labels', Icon: TagIcon },
  { title: 'Attach CTAs', Icon: MousePointerClickIcon },
  { title: 'Set follow-ups', Icon: CornerDownRightIcon },
  { title: 'Set context type', Icon: SearchIcon },
  { title: 'Delete questions', Icon: Trash2Icon },
  { title: 'Reorder questions', Icon: ListOrderedIcon },
  { title: 'Bulk changes', Icon: LayersIcon },
  { title: 'Refer by number & tab', Icon: SparklesIcon },
  { title: 'Combine actions', Icon: Wand2Icon },
];

// Scalar fields that map straight onto the question update/create payload.
const PATCH_KEYS = [
  'question',
  'answer',
  'answer_summary',
  'is_first_class',
  'is_lars',
  'is_active',
  'is_faq',
  'is_conversion',
  'is_optimizable',
  'context_type',
] as const;

const buildPatch = (after: QuestionOp['after']): Partial<PrimerQuestionRequest> => {
  const patch: Record<string, any> = {};
  if (!after) return patch;
  for (const k of PATCH_KEYS) {
    if ((after as any)[k] !== undefined) patch[k] = (after as any)[k];
  }
  return patch;
};

let msgSeq = 0;
const newId = () => `m${Date.now()}_${msgSeq++}`;

const QuestionAIAssistantDrawer: React.FC<QuestionAIAssistantDrawerProps> = ({
  isOpen,
  onClose,
  questionIds,
  tabOrders,
  activeTab,
  filters,
  onApplied,
}) => {
  // Restore any persisted session once, on mount.
  const persisted = useMemo(
    () => loadJSON<QPersist>(QKEY, { messages: [], pendingOps: [], decisions: {}, statuses: {}, wide: false }),
    [],
  );

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(persisted.messages || []);
  // A single, accumulating set of proposed (not-yet-applied) changes. New turns
  // merge into this; a change targeting the same question replaces the older one.
  const [pendingOps, setPendingOps] = useState<QuestionOp[]>(persisted.pendingOps || []);
  // Per-proposal decision: 'approved' | 'rejected' (absent = pending / undecided).
  const [decisions, setDecisions] = useState<Record<string, Decision>>(persisted.decisions || {});
  // Drop any transient 'applying' status left over from a reload mid-apply.
  const [statuses, setStatuses] = useState<Record<string, OpStatus>>(
    Object.fromEntries(
      Object.entries(persisted.statuses || {}).filter(([, v]) => v !== 'applying'),
    ) as Record<string, OpStatus>,
  );
  // Latch the wide (diff) layout once changes have appeared, so it doesn't jump
  // back to narrow after applying/clearing mid-conversation.
  const [wide, setWide] = useState(!!persisted.wide);
  // Pending-changes panel collapse: auto-collapses when a new message is sent
  // (focus returns to the conversation), auto-expands when new proposals arrive.
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  // "What I can do" capabilities modal.
  const [showHelp, setShowHelp] = useState(false);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const didInitScrollRef = useRef(false);

  // Persist the session on every change (cap stored messages).
  useEffect(() => {
    saveJSON(QKEY, { messages: messages.slice(-100), pendingOps, decisions, statuses, wide });
  }, [messages, pendingOps, decisions, statuses, wide]);

  const started = messages.length > 0;
  const approvedCount = pendingOps.filter(
    (o) => decisions[o.op_id] === 'approved' && statuses[o.op_id] !== 'applied',
  ).length;

  // Merge new ops into the pending set: same-question changes are replaced by the
  // latest (conflict → latest wins); everything else is appended.
  const conflictKey = (op: QuestionOp): string | null =>
    op.action === 'create' ? null : op.question_id || null;

  const mergePendingOps = (incoming: QuestionOp[]) => {
    if (!incoming.length) return;
    setWide(true);
    setPanelCollapsed(false); // new proposals → expand and show them
    setPendingOps((prev) => {
      const next = [...prev];
      for (const op of incoming) {
        const key = conflictKey(op);
        const idx = key ? next.findIndex((o) => conflictKey(o) === key) : -1;
        if (idx >= 0) next[idx] = op; // latest takes precedence
        else next.push(op);
      }
      return next;
    });
    // New/replaced proposals start undecided (pending) — no entry in `decisions`.
  };

  // Focus composer on open.
  useEffect(() => {
    if (isOpen && !loading && !applying) inputRef.current?.focus();
  }, [isOpen, loading, applying]);

  // Auto-grow composer with content (capped), immediate as text lengthens.
  const resizeComposer = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  };
  useEffect(() => {
    resizeComposer(inputRef.current);
  }, [input, isOpen]);

  // Conversation anchoring: on every new message (question or reply) scroll the
  // latest user message up as far as the content allows (browser clamps it, so no
  // reserved blank space) — keeping focus on the newest exchange.
  const anchorIndex = (() => {
    for (let i = messages.length - 1; i >= 0; i--) if (messages[i].role === 'user') return i;
    return -1;
  })();

  useLayoutEffect(() => {
    const c = scrollRef.current;
    if (!c) return;
    if (!didInitScrollRef.current) {
      didInitScrollRef.current = true;
      c.scrollTop = c.scrollHeight; // first render → show newest
      return;
    }
    const a = anchorRef.current;
    c.scrollTop = a ? Math.max(0, a.offsetTop - 12) : c.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, loading]);

  // Once every change is applied and nothing is pending, return to normal width.
  useEffect(() => {
    if (pendingOps.length === 0) setWide(false);
  }, [pendingOps.length]);

  // ESC to close.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !applying) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, applying, onClose]);

  const send = async (text: string, opts?: { questionIds?: string[] }) => {
    const trimmed = text.trim();
    if (!trimmed || loading || applying) return;

    const history = messages.map((m) => ({ role: m.role, content: m.text })).slice(-8) as Array<{
      role: 'user' | 'assistant';
      content: string;
    }>;

    setMessages((prev) => [...prev, { id: newId(), role: 'user', text: trimmed }]);
    setInput('');
    setLoading(true);
    setPanelCollapsed(true); // focus the conversation; re-expands if new changes come back

    try {
      const result = await tenantApi.planQuestionOps({
        message: trimmed,
        conversation_history: history,
        question_ids: opts?.questionIds ?? (questionIds && questionIds.length ? questionIds : undefined),
        tab_orders: tabOrders && Object.keys(tabOrders).length ? tabOrders : undefined,
        active_tab: activeTab,
        filters,
      });
      const ops = result.operations || [];
      // Tolerate an older backend build that still returns `summary` instead of `reply`.
      const replyText = (result.reply ?? (result as any).summary ?? '') as string;
      const text = replyText || (ops.length ? '' : 'I don’t have a response for that.');
      setMessages((prev) => [
        ...prev,
        { id: newId(), role: 'assistant', text, warnings: result.warnings || [], suggestions: result.suggestions || [] },
      ]);
      mergePendingOps(ops);
    } catch {
      setMessages((prev) => [
        ...prev,
        { id: newId(), role: 'assistant', text: '⚠️ Something went wrong. Please try again.', warnings: [], suggestions: [] },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  // Per-proposal decisions (all reversible via Undo, which clears the decision).
  const decideOp = (opId: string, d: Decision) => setDecisions((prev) => ({ ...prev, [opId]: d }));
  const undoOp = (opId: string) =>
    setDecisions((prev) => {
      const next = { ...prev };
      delete next[opId];
      return next;
    });

  // Toolbar "…all" actions apply to every not-yet-applied proposal.
  const decideAll = (d: Decision) =>
    setDecisions((prev) => ({
      ...prev,
      ...Object.fromEntries(pendingOps.filter((o) => statuses[o.op_id] !== 'applied').map((o) => [o.op_id, d])),
    }));
  const undoAll = () => setDecisions({});

  // Refine a single proposal — send a scoped instruction so the AI returns an
  // updated change for that question (which replaces the old one via the merge).
  const refineOp = (op: QuestionOp, instruction: string) => {
    const idPart = op.question_id ? ` (question id ${op.question_id})` : '';
    send(`Refine your proposed ${op.action} for "${op.label}"${idPart}: ${instruction}`, {
      questionIds: op.question_id ? [op.question_id] : undefined,
    });
  };

  const applyPending = async () => {
    const targets = pendingOps.filter((o) => decisions[o.op_id] === 'approved' && statuses[o.op_id] !== 'applied');
    if (!targets.length || applying) return;
    setApplying(true);

    // Resolve label names → ids once, creating any missing.
    const labelMap = new Map<string, string>();
    try {
      const labels = await tenantApi.getQuestionLabels();
      labels.forEach((l) => labelMap.set(l.name.toLowerCase(), l.id));
    } catch {
      /* labels best-effort */
    }
    const resolveLabelId = async (name: string): Promise<string | null> => {
      const key = name.toLowerCase();
      const existing = labelMap.get(key);
      if (existing) return existing;
      try {
        const created = await tenantApi.createQuestionLabel(name);
        labelMap.set(key, created.id);
        return created.id;
      } catch {
        return null;
      }
    };

    const appliedIds: string[] = [];
    for (const op of targets) {
      setStatuses((s) => ({ ...s, [op.op_id]: 'applying' }));
      try {
        if (op.action === 'create') {
          const after = op.after || {};
          const req: PrimerQuestionRequest = {
            question: after.question || '',
            answer: after.answer || '',
            is_active: after.is_active !== false,
            is_root: true,
            is_primer: true,
            is_conversion: after.is_conversion !== false,
            is_faq: !!after.is_faq,
            is_first_class: !!after.is_first_class,
            order_index: 0,
            context_type: after.context_type ?? null,
          };
          const created = await tenantApi.createPrimerQuestion(req);
          const addIds = (await Promise.all((after.add_labels || []).map(resolveLabelId))).filter(
            (x): x is string => !!x,
          );
          if (addIds.length) await tenantApi.assignLabelsToQuestion(created.id, addIds);
        } else if (op.action === 'update' && op.question_id) {
          const patch = buildPatch(op.after);
          if (Object.keys(patch).length) await tenantApi.updatePrimerQuestion(op.question_id, patch);
          const addIds = (await Promise.all((op.after?.add_labels || []).map(resolveLabelId))).filter(
            (x): x is string => !!x,
          );
          if (addIds.length) await tenantApi.assignLabelsToQuestion(op.question_id, addIds);
          for (const name of op.after?.remove_labels || []) {
            const id = labelMap.get(name.toLowerCase());
            if (id) await tenantApi.removeLabelFromQuestion(op.question_id, id);
          }
          // Follow-ups (parent → child links).
          for (const p of op.after?.add_parents || []) await tenantApi.addParentToQuestion(op.question_id, p.id);
          for (const p of op.after?.remove_parents || []) await tenantApi.removeParentFromQuestion(op.question_id, p.id);
          for (const c of op.after?.add_children || []) await tenantApi.addParentToQuestion(c.id, op.question_id);
          for (const c of op.after?.remove_children || []) await tenantApi.removeParentFromQuestion(c.id, op.question_id);
          // CTAs.
          for (const c of op.after?.add_ctas || []) await tenantApi.assignCTAToPrimerQuestion(op.question_id, c.id);
          for (const c of op.after?.remove_ctas || []) await tenantApi.removeCTAFromPrimerQuestion(op.question_id, c.id);
        } else if (op.action === 'delete' && op.question_id) {
          await tenantApi.deletePrimerQuestion(op.question_id);
        } else if (op.action === 'reorder') {
          for (const item of op.after?.reorder || []) {
            await tenantApi.updatePrimerQuestion(item.id, { order_index: item.order_index });
          }
        }
        setStatuses((s) => ({ ...s, [op.op_id]: 'applied' }));
        appliedIds.push(op.op_id);
      } catch {
        setStatuses((s) => ({ ...s, [op.op_id]: 'failed' }));
        toast.error(`Failed to apply: ${op.label}`);
      }
    }

    try {
      await tenantApi.clearPromptCache();
    } catch {
      /* non-fatal */
    }

    const appliedCount = appliedIds.length;
    if (appliedCount) {
      // Applied changes leave the list entirely; unapplied/failed ones stay for review.
      const remaining = pendingOps.filter((o) => !appliedIds.includes(o.op_id));
      setPendingOps(remaining);
      setDecisions((prev) => {
        const next = { ...prev };
        for (const id of appliedIds) delete next[id];
        return next;
      });
      // Nothing left to act on (empty, or only rejected) → collapse the panel.
      const actionable = remaining.filter((o) => decisions[o.op_id] !== 'rejected');
      if (actionable.length === 0) setPanelCollapsed(true);
      // Keep the conversation going — leave every message in place, add a short
      // confirmation, and hand focus back to the composer.
      setMessages((prev) => [
        ...prev,
        {
          id: newId(),
          role: 'assistant',
          kind: 'applied',
          text: `Applied ${appliedCount} change${appliedCount === 1 ? '' : 's'}. What would you like to do next?`,
          warnings: [],
          suggestions: ['Suggest another improvement', 'Review questions with weak answers'],
        },
      ]);
      // Keep the Apply bar showing "Applying…" until the page refresh finishes too.
      await onApplied();
      toast.success(`Applied ${appliedCount} change${appliedCount === 1 ? '' : 's'}`);
      inputRef.current?.focus();
    }
    setApplying(false);
  };

  const resetChat = () => {
    setMessages([]);
    setPendingOps([]);
    setDecisions({});
    setStatuses({});
    setWide(false);
    setPanelCollapsed(false);
    removeKey(QKEY);
    inputRef.current?.focus();
  };

  const renderMessage = (m: ChatMessage) =>
    m.role === 'user' ? (
      <div key={m.id} className="flex justify-end">
        <div className="max-w-[80%] bg-indigo-600 text-white rounded-tl-xl rounded-b-xl px-4 py-2.5 text-sm leading-relaxed shadow-sm whitespace-pre-wrap">
          {m.text}
        </div>
      </div>
    ) : (
      <div key={m.id} className="flex items-start gap-2.5">
        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center mt-0.5">
          <AiIcon />
        </div>
        <div className="min-w-0 flex-1 space-y-3">
          {m.text && (
            <div className="inline-flex items-start gap-2 max-w-full bg-gray-100 rounded-tr-xl rounded-b-xl px-4 py-3 text-sm shadow-sm">
              {m.kind === 'applied' && (
                <CheckCircle2Icon className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
              )}
              <MarkdownRenderer content={m.text} className="text-gray-800" />
            </div>
          )}

          {m.warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
              <AlertTriangleIcon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>{w}</span>
            </div>
          ))}

          {m.suggestions.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-0.5">
              {m.suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  disabled={loading || applying}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 hover:bg-indigo-100 hover:border-indigo-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <SparklesIcon className="h-3 w-3 text-indigo-400" />
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );

  const TypingBubble = (
    <div className="flex items-start gap-2.5">
      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center mt-0.5">
        <AiIcon />
      </div>
      <div className="bg-gray-100 rounded-tr-xl rounded-b-xl px-4 py-3 flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.3s]" />
        <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.15s]" />
        <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" />
      </div>
    </div>
  );

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-20 z-40 transition-opacity" onClick={applying ? undefined : onClose} />
      )}

      <div
        className={`fixed top-0 right-0 h-full bg-white shadow-2xl z-[5000000000] flex flex-col transform transition-[transform,width] duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'
          } ${wide ? 'w-[920px] max-w-[96vw]' : 'w-[480px] max-w-full'}`}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center">
            <AiIcon />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-base font-semibold text-gray-900">AI Question Assistant</span>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
              <span className="text-xs text-gray-500 truncate">Ask, analyze &amp; edit • you approve every change</span>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={() => setShowHelp(true)}
              title="What I can do"
              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors rounded"
            >
              <InfoIcon className="h-4 w-4" />
            </button>
            {started && (
              <button
                onClick={resetChat}
                disabled={applying}
                title="New chat"
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors rounded disabled:opacity-40"
              >
                <Trash2Icon className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={applying ? undefined : onClose}
              disabled={applying}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-indigo-100 transition-colors rounded disabled:opacity-40"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Thread */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
          {!started && !loading ? (
            <div className="flex flex-col items-center text-center pt-10">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center mb-5">
                <AiIcon />
              </div>
              <h3 className="text-xl font-bold text-gray-900 tracking-tight">Chat with your question library</h3>
              <p className="mt-2 text-sm text-gray-500 leading-relaxed max-w-sm">
                Ask, analyze, or request a change in plain language. Any edits I suggest appear as a
                side-by-side diff you approve • nothing changes until you say so.
              </p>
              <div className="mt-7 w-full grid grid-cols-1 max-w-[440px] gap-2.5">
                {SUGGESTIONS.map(({ icon: Icon, text }) => (
                  <button
                    key={text}
                    onClick={() => send(text)}
                    className="group w-full flex items-center gap-3 px-3.5 py-3 text-sm text-gray-700 bg-white border border-gray-200 hover:bg-indigo-50/40 hover:border-indigo-200 transition-colors text-left rounded-lg"
                  >
                    <span className="flex-shrink-0 w-7 h-7 rounded-md bg-indigo-50 text-indigo-500 flex items-center justify-center group-hover:bg-indigo-100">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="flex-1">{text}</span>
                    <ArrowRightIcon className="h-4 w-4 text-gray-300 group-hover:text-indigo-400 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {anchorIndex > 0 && (
                <div className="space-y-4">{messages.slice(0, anchorIndex).map(renderMessage)}</div>
              )}
              {/* Latest turn — scrolled to the top on each new message. */}
              <div ref={anchorRef} className={`space-y-4 ${anchorIndex > 0 ? 'pt-4' : ''}`}>
                {(anchorIndex >= 0 ? messages.slice(anchorIndex) : messages).map(renderMessage)}
                {loading && TypingBubble}
              </div>
            </>
          )}
        </div>

        {/* Consolidated pending changes — DOCKED at the bottom of the container
            (below the scrolling thread), collapsible, with its own internal scroll. */}
        {pendingOps.length > 0 && (
          <div className="flex-shrink-0 border-t border-gray-100 bg-gray-50/40 px-4 py-3">
            <ProposalPanel
              ops={pendingOps}
              decisions={decisions}
              statuses={statuses}
              collapsed={panelCollapsed}
              onToggleCollapsed={() => setPanelCollapsed((v) => !v)}
              onApproveOp={(id) => decideOp(id, 'approved')}
              onRejectOp={(id) => decideOp(id, 'rejected')}
              onUndoOp={undoOp}
              onRefine={refineOp}
              onApproveAll={() => decideAll('approved')}
              onRejectAll={() => decideAll('rejected')}
              onUndoAll={undoAll}
            />
          </div>
        )}

        {/* Sticky Apply bar — visible while any change is approved, and stays
            through applying + the page refresh (only hides once both finish). */}
        {(approvedCount > 0 || applying) && (
          <div className="px-4 py-2.5 border-t border-gray-100 bg-white flex-shrink-0">
            <button
              onClick={applyPending}
              disabled={applying || approvedCount === 0}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded text-sm font-semibold text-white bg-gradient-to-r from-indigo-500 to-violet-500 hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {applying ? <Loader2Icon className="h-4 w-4 animate-spin" /> : null}
              {applying
                ? 'Applying & refreshing…'
                : `Apply ${approvedCount} approved change${approvedCount === 1 ? '' : 's'}`}
            </button>
          </div>
        )}

        {/* Composer */}
        <div className="px-4 py-3.5 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <div
            className={`flex items-end gap-2 bg-white border rounded-xl px-3 py-2 transition-all ${loading || applying
              ? 'border-gray-200'
              : 'border-gray-200 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-500/20'
              }`}
          >
            <SparklesIcon className="h-4 w-4 text-indigo-400 mb-1.5 flex-shrink-0" />
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              placeholder={started ? 'Ask a follow-up or request another change…' : 'Ask a question or describe a change…'}
              disabled={loading || applying}
              className="flex-1 text-sm text-gray-800 bg-transparent outline-none placeholder-gray-400 resize-none leading-6 py-0.5 max-h-40 disabled:cursor-wait"
            />
            <button
              onClick={() => send(input)}
              disabled={loading || applying || !input.trim()}
              className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-white hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
              title="Send"
            >
              {loading ? <Loader2Icon className="h-4 w-4 animate-spin" /> : <SendIcon className="h-4 w-4" />}
            </button>
          </div>
          <p className="mt-1.5 px-1 text-[11px] text-gray-400">
            <kbd className="font-sans font-medium text-gray-500">Enter</kbd> to send ·{' '}
            <kbd className="font-sans font-medium text-gray-500">Shift</kbd>+
            <kbd className="font-sans font-medium text-gray-500">Enter</kbd> for a new line
          </p>
        </div>
      </div>

      {showHelp && <CapabilitiesModal onClose={() => setShowHelp(false)} />}
    </>
  );
};

// ---------------------------------------------------------------------------
// "What I can do" capabilities modal.
// ---------------------------------------------------------------------------

const CapabilitiesModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[6000000000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[85vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden animate-fade-in-up">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center">
            <AiIcon />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900">What I can do</h3>
            <p className="text-xs text-gray-500">Ask in plain language • every change is reviewed before it applies.</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors">
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {CAPABILITIES.map(({ title, Icon }) => (
            <div key={title} className="flex items-center gap-2.5 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2.5">
              <span className="w-7 h-7 rounded-md bg-indigo-50 text-indigo-500 flex items-center justify-center flex-shrink-0">
                <Icon className="h-4 w-4" />
              </span>
              <span className="text-sm font-medium text-gray-800">{title}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Inline proposal panel — the diff editor for one assistant turn's changes.
// ---------------------------------------------------------------------------

const ProposalPanel: React.FC<{
  ops: QuestionOp[];
  decisions: Record<string, Decision>;
  statuses: Record<string, OpStatus>;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onApproveOp: (opId: string) => void;
  onRejectOp: (opId: string) => void;
  onUndoOp: (opId: string) => void;
  onRefine: (op: QuestionOp, instruction: string) => void;
  onApproveAll: () => void;
  onRejectAll: () => void;
  onUndoAll: () => void;
}> = ({
  ops,
  decisions,
  statuses,
  collapsed,
  onToggleCollapsed,
  onApproveOp,
  onRejectOp,
  onUndoOp,
  onRefine,
  onApproveAll,
  onRejectAll,
  onUndoAll,
}) => {
    const live = ops.filter((o) => statuses[o.op_id] !== 'applied');
    const approvedCount = live.filter((o) => decisions[o.op_id] === 'approved').length;
    const decidedCount = live.filter((o) => decisions[o.op_id]).length;

    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50/60">
        {/* Header — click to collapse/expand */}
        <div className="flex items-center justify-between px-3 py-2">
          <button
            onClick={onToggleCollapsed}
            className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-800 min-w-0"
            title={collapsed ? 'Expand pending changes' : 'Collapse pending changes'}
          >
            {collapsed ? (
              <ChevronRightIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
            ) : (
              <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
            )}
            <ListChecksIcon className="h-3.5 w-3.5 flex-shrink-0" />
            <span>Pending changes</span>
            <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] leading-none">
              {live.length}
            </span>
            <span className="text-gray-400 font-normal truncate">· {approvedCount} approved</span>
          </button>
          {!collapsed && (
            <div className="flex items-center gap-2 flex-shrink-0 text-xs">
              <button onClick={onApproveAll} className="font-medium text-indigo-600 hover:text-indigo-700">
                Approve all
              </button>
              <span className="text-gray-300">·</span>
              <button onClick={onRejectAll} className="font-medium text-rose-600 hover:text-rose-700">
                Reject all
              </button>
              {decidedCount > 0 && (
                <>
                  <span className="text-gray-300">·</span>
                  <button onClick={onUndoAll} className="font-medium text-gray-500 hover:text-gray-700">
                    Undo all
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {!collapsed && (
          <div className="px-3 pb-3 space-y-3 max-h-[46vh] overflow-y-auto">
            {ops.map((op, i) => (
              <OpDiffCard
                key={op.op_id}
                op={op}
                index={i}
                decision={decisions[op.op_id]}
                status={statuses[op.op_id]}
                onApprove={() => onApproveOp(op.op_id)}
                onReject={() => onRejectOp(op.op_id)}
                onUndo={() => onUndoOp(op.op_id)}
                onRefine={(instruction) => onRefine(op, instruction)}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

export default QuestionAIAssistantDrawer;
